import { editorClipboardEnvelopeSchema, type EditorClipboardEnvelope } from '@personalizehq/schemas';

const STORAGE_KEY = 'personalizehq:builder-clipboard:v1';
const PREFIX = 'personalizehq://subtrees/v1\n';
let memory: EditorClipboardEnvelope | null = null;

export async function writeEditorClipboard(envelope: EditorClipboardEnvelope): Promise<'system' | 'internal'> {
  memory = envelope;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(envelope));
  } catch {
    // In-memory clipboard remains available for the current editor session.
  }
  const writeText = navigator.clipboard?.writeText;
  if (!writeText) return 'internal';
  try {
    await writeText.call(navigator.clipboard, `${PREFIX}${JSON.stringify(envelope)}`);
    return 'system';
  } catch {
    return 'internal';
  }
}

export async function readEditorClipboard(): Promise<EditorClipboardEnvelope | null> {
  try {
    const text = await navigator.clipboard?.readText();
    if (text?.startsWith(PREFIX)) {
      const parsed = parseEnvelope(text.slice(PREFIX.length));
      if (parsed) return parsed;
    }
  } catch {
    // Permission-denied and unsupported browsers fall back to the editor clipboard.
  }
  if (memory) return memory;
  try {
    return parseEnvelope(localStorage.getItem(STORAGE_KEY));
  } catch {
    return null;
  }
}

export function readInternalEditorClipboard(): EditorClipboardEnvelope | null {
  if (memory) return memory;
  try {
    return parseEnvelope(localStorage.getItem(STORAGE_KEY));
  } catch {
    return null;
  }
}

function parseEnvelope(raw: string | null | undefined): EditorClipboardEnvelope | null {
  if (!raw) return null;
  try {
    const parsed = editorClipboardEnvelopeSchema.safeParse(JSON.parse(raw));
    return parsed.success ? parsed.data : null;
  } catch {
    return null;
  }
}
